import {useState, useMemo, useCallback, useEffect} from 'react'
import type {HTMLAttributes} from 'react'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {ownerAvatarPath} from '@github-ui/paths'
import {ActionList, Button, SelectPanel} from '@primer/react'
import type {SelectPanelItemInput as ItemInput} from '@primer/react'
import {TriangleDownIcon} from '@primer/octicons-react'
import type {FilterOption} from '../../utils/filter-utils'
import {useCollaboratorSearch} from '../../hooks/use-collaborator-search'

export interface CreatorSelectPanelProps {
  creatorOptions: FilterOption[]
  selectedCreators: string[]
  onCreatorChange: (creators: string[]) => void
  owner: string
  repo: string
  nested?: boolean
  className?: string
}

export function CreatorSelectPanel({
  creatorOptions,
  selectedCreators,
  onCreatorChange,
  owner,
  repo,
  nested,
  className,
}: CreatorSelectPanelProps) {
  const [open, setOpen] = useState(false)
  const [filter, setFilter] = useState('')
  const [debouncedFilter, setDebouncedFilter] = useState('')

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedFilter(filter), 250)
    return () => clearTimeout(timer)
  }, [filter])

  const {data: searchResults, isLoading: isSearching} = useCollaboratorSearch(owner, repo, debouncedFilter)

  const items: ItemInput[] = useMemo(() => {
    const seen = new Map<string, ItemInput>()

    const toItem = (login: string, avatarUrl?: string): ItemInput => ({
      key: login,
      text: login,
      leadingVisual: avatarUrl
        ? () => <GitHubAvatar src={avatarUrl} size={20} alt="" />
        : () => <GitHubAvatar src={ownerAvatarPath({owner: login})} size={20} alt="" />,
    })

    for (const opt of creatorOptions) {
      const key = opt.value.toLowerCase()
      if (!seen.has(key)) seen.set(key, toItem(opt.value, opt.avatarUrl))
    }

    if (searchResults) {
      for (const result of searchResults) {
        const key = result.login.toLowerCase()
        if (!seen.has(key)) seen.set(key, toItem(result.login, result.avatarUrl))
      }
    }

    for (const login of selectedCreators) {
      const key = login.toLowerCase()
      if (!seen.has(key)) seen.set(key, toItem(login))
    }

    return Array.from(seen.values())
  }, [creatorOptions, searchResults, selectedCreators])

  const filteredItems = useMemo(() => {
    if (!filter) return items
    const lower = filter.toLowerCase()
    return items.filter(item => item.text?.toLowerCase().includes(lower))
  }, [items, filter])

  const selected = useMemo(
    () => items.filter(item => selectedCreators.includes(item.key as string)),
    [items, selectedCreators],
  )

  const handleSelectedChange = useCallback(
    (newSelected: ItemInput[]) => {
      onCreatorChange(newSelected.map(item => item.key as string))
    },
    [onCreatorChange],
  )

  if (creatorOptions.length === 0 && !filter) return null

  const renderAnchor = <T extends HTMLAttributes<HTMLElement>>(props: T) =>
    nested ? (
      <ActionList.Item {...props} role="menuitem" aria-haspopup="dialog" aria-label="Author">
        Author...
      </ActionList.Item>
    ) : (
      <Button
        variant="invisible"
        trailingVisual={TriangleDownIcon}
        aria-haspopup="dialog"
        className={className}
        count={selectedCreators.length || undefined}
        {...props}
      >
        <span>Author</span>
      </Button>
    )

  return (
    <SelectPanel
      title="Filter by author"
      renderAnchor={renderAnchor}
      placeholderText="Search authors..."
      items={filteredItems}
      selected={selected}
      onSelectedChange={handleSelectedChange}
      onFilterChange={setFilter}
      open={open}
      onOpenChange={setOpen}
      overlayProps={{width: 'small', height: filteredItems.length > 6 ? 'small' : 'auto'}}
      loading={isSearching && filter.length > 0}
      message={
        filteredItems.length === 0 && !isSearching
          ? {title: 'No authors found', body: 'Try a different search term.', variant: 'empty' as const}
          : undefined
      }
    />
  )
}
